<template>
  <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden flex flex-col">
    <div class="px-5 py-3.5 border-b border-gray-100 dark:border-gray-700 flex items-start justify-between gap-3">
      <div class="min-w-0">
        <p class="text-sm font-semibold text-gray-900 dark:text-white">{{ title }}</p>
        <p v-if="subtitle" class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{{ subtitle }}</p>
      </div>
      <div class="flex items-center gap-2 flex-shrink-0">
        <slot name="actions" />
        <!-- A handful of panels describe the present rather than the selected
             window. Saying so is what stops someone changing the range, seeing
             this not move, and filing it as a bug. -->
        <span v-if="asOfNow"
          class="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 whitespace-nowrap">
          as of now
        </span>
      </div>
    </div>

    <!-- Skeletons mirror the shape of what they replace rather than being a
         grey rectangle, so the first paint does not reflow into the real thing. -->
    <div v-if="loading" class="animate-pulse flex-1" :style="{ minHeight: `${height}px` }">
      <div v-if="skeleton === 'chart'" class="p-5 flex flex-col justify-end gap-2 h-full">
        <div class="flex items-end gap-1.5 flex-1">
          <div v-for="i in 14" :key="i" class="flex-1 bg-gray-100 dark:bg-gray-800 rounded-t"
            :style="{ height: `${25 + Math.abs(Math.sin(i * 1.7)) * 65}%` }"></div>
        </div>
        <div class="h-2 bg-gray-100 dark:bg-gray-800 rounded w-full"></div>
      </div>

      <div v-else-if="skeleton === 'donut'" class="p-5 flex items-center gap-6 h-full">
        <div class="w-32 h-32 rounded-full border-[16px] border-gray-100 dark:border-gray-800 flex-shrink-0"></div>
        <div class="flex-1 space-y-3">
          <div v-for="i in 4" :key="i" class="flex items-center gap-3">
            <div class="w-2.5 h-2.5 rounded-full bg-gray-200 dark:bg-gray-700"></div>
            <div class="h-3 bg-gray-200 dark:bg-gray-700 rounded flex-1"></div>
            <div class="h-3 bg-gray-100 dark:bg-gray-800 rounded w-8"></div>
          </div>
        </div>
      </div>

      <div v-else class="divide-y divide-gray-50 dark:divide-gray-800/60">
        <div v-for="i in rows" :key="i" class="flex items-center gap-3 px-5 py-3">
          <div class="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-700 flex-shrink-0"></div>
          <div class="h-3 bg-gray-200 dark:bg-gray-700 rounded flex-1"></div>
          <div class="h-3 bg-gray-100 dark:bg-gray-800 rounded w-10 flex-shrink-0"></div>
        </div>
      </div>
    </div>

    <div v-else-if="empty" class="flex flex-col items-center justify-center px-6 text-center flex-1"
      :style="{ minHeight: `${height}px` }">
      <div class="w-11 h-11 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-3">
        <svg class="w-5 h-5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625Z"/>
        </svg>
      </div>
      <p class="text-sm font-medium text-gray-600 dark:text-gray-300">{{ emptyTitle }}</p>
      <p v-if="emptyHint" class="text-xs text-gray-400 dark:text-gray-500 mt-1 max-w-xs leading-relaxed">{{ emptyHint }}</p>
    </div>

    <div v-else class="flex-1 min-w-0"><slot /></div>
  </div>
</template>

<script setup>
// One place for the chrome every panel on this page shares: header, skeleton,
// empty state. Without it each panel reimplements three states slightly
// differently and the dashboard stops feeling like one dashboard.
defineProps({
  title:      { type: String, required: true },
  subtitle:   { type: String, default: '' },
  loading:    { type: Boolean, default: false },
  empty:      { type: Boolean, default: false },
  emptyTitle: { type: String, default: 'Nothing to show yet' },
  emptyHint:  { type: String, default: '' },
  asOfNow:    { type: Boolean, default: false },
  height:     { type: Number, default: 256 },
  // Which skeleton shape to draw: chart bars, a donut, or table rows.
  skeleton:   { type: String, default: 'chart' },
  rows:       { type: Number, default: 6 },
})
</script>
